import { Dispatch, ExtraArgs, GetState } from '../../types'
import { SelectorAction } from '../redux/selector/model'
import { APIDetail, APIStatus, Application, ARMApplicationDetail, ARMHybridDetail, ARMServer, Asset, RTFApplication } from '../../remote-api/models'
import { FetchAction } from '../redux/fetch/model'
import {
  API_RESOURCE_NAME,
  ApplicationResource,
  CH_RESOURCE_NAME,
  DetailedResource,
  ENDPOINT_STATUS,
  getAPIFromAPIInstance,
  getResourceByIdOrInfluxId,
  getResourceId,
  HYBRID_RESOURCE_NAME,
  inactivityThreshold,
  isApplicationResource,
  isAsset,
  isManagedAPI, isTarget,
  MANAGED_API_RESOURCE_NAME,
  Resource,
  RESOURCE_PREFIX,
  RESOURCE_RESOURCE_NAME,
  resourceNameComparator,
  RTF_RESOURCE_NAME,
  TARGET_RESOURCE_NAME
} from './model'
import { fetchLoaderHelper } from '../redux/loader/thunksFor'
import { getApps } from '../../remote-api/MergeRemoteApi'
import { isHybrid, isRTF } from '../../components/BuiltInDashboard/common/hybrid'
import { selectResourceFromResources } from '../redux/selector/thunksFor'
import { changePlatformToken, selectors as authSelectors } from '../../lifecycle/auth'
import selectorActionCreatorsFor from '../redux/selector/actionCreatorsFor'
import fetchActionCreatorsFor from '../redux/fetch/actionCreatorsFor'
import ApplicationService from '../../services/core/ApplicationService'
import { SelectorOptions } from '../selector/model'
import { ResourceSelectorState } from './reducer'
import { ManagedAPI } from '@mulesoft/monitoring-center-components/lib/models/api-manager'
import { ResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'

// Resources

export type ResourceAction =
  | SelectorAction<Resource>
  | FetchAction<ApplicationResource[]>
  | FetchAction<ARMApplicationDetail[]>
  | FetchAction<ARMHybridDetail[]>
  | FetchAction<RTFApplication[]>
  | FetchAction<ARMServer[]>
  | FetchAction<Asset[]>
  | FetchAction<ManagedAPI[]>

export const resourceActionCreators = (origin?: string) => {
  return {
    ...selectorActionCreatorsFor<Resource>(RESOURCE_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const fetchAllResources = (options: SelectorOptions, envId: string) =>
  (dispatch: Dispatch): Promise<Resource[]> => {
    return fetchLoaderHelper<Resource[]>(dispatch, resourceActionCreators(options.origin), async () => {
      const isTargetDashboardsEnabled = window.mc.featureFlags && window.mc.featureFlags.builtIn.targetDashboards.enabled === 'true'

      const promises: [Promise<ApplicationResource[]>, Promise<ARMServer[]>, Promise<Asset[]>, Promise<ManagedAPI[]>] = [
        dispatch(fetchAllApps(options, envId)),
        isTargetDashboardsEnabled ? dispatch(fetchServers(options, envId)) : Promise.resolve([]),
        dispatch(fetchAPIsWithToken(envId, options)),
        dispatch(fetchManagedAPIsWithToken(envId, options))
      ]
      return Promise.all(promises).then(results => {
        return [...results[0], ...results[1], ...results[2], ...results[3]].sort(resourceNameComparator)
      })
    },                                   true)
  }

export const fetchLoadingResources = (options: SelectorOptions, envId: string, resourceSelector: ResourceSelectorState) =>
  (dispatch: Dispatch): Promise<Resource[]> => {
    return fetchLoaderHelper<Resource[]>(dispatch, resourceActionCreators(options.origin), async () => {
      const isTargetDashboardsEnabled = window.mc.featureFlags && window.mc.featureFlags.builtIn.targetDashboards.enabled === 'true'
      const promises: any[] = []

      if (resourceSelector.managedApis.isFetching) promises.push(dispatch(fetchManagedAPIsWithToken(envId, options)))
      if (resourceSelector.apis.isFetching) promises.push(dispatch(fetchAPIsWithToken(envId, options)))
      if (resourceSelector.servers.isFetching && isTargetDashboardsEnabled) promises.push(dispatch(fetchServers(options, envId)))
      if (resourceSelector.cloudHub.isFetching) promises.push(dispatch(fetchCHApps(options, envId)))
      if (resourceSelector.hybrid.isFetching) promises.push(dispatch(fetchHybridApps(options, envId)))
      if (resourceSelector.rtf.isFetching) promises.push(dispatch(fetchRTFApps(options, envId)))

      return Promise.all(promises).then(results => {
        let result: any[] = []

        results.forEach(resources => result = result.concat(resources))
        return result.sort(resourceNameComparator)
      })
    },                                   true)
}

export const fetchDetailedResource = (envId: string, resource: Resource, versionId?: string) => (dispatch: Dispatch): Promise<DetailedResource> => {
  if (isApplicationResource(resource) && !versionId) {
    return dispatch(fetchDetailedApp(envId, resource))
  }

  if (isTarget(resource)) {
    return Promise.resolve(resource)
  }

  if (versionId && isAsset(resource)) {
    return dispatch(fetchDetailedAPIWithToken(envId, versionId, resource.id.toString()))
  }

  if (isManagedAPI(resource)) {
    return Promise.resolve(resource)
  }

  throw new Error('Its neither an asset with versionId, nor an app without versionId, nor a target, nor a DataGraph.')
}

// Applications

export const cloudhubAppActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<ARMApplicationDetail[]>(CH_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const hybridAppActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<ARMHybridDetail[]>(HYBRID_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const rtfAppActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<RTFApplication[]>(RTF_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const fetchAllApps = (options: SelectorOptions, envId: string) => (dispatch: Dispatch): Promise<ApplicationResource[]> => {
  const promises: [Promise<ARMApplicationDetail[]>, Promise<ARMHybridDetail[]>, Promise<RTFApplication[]>] = [
    dispatch(fetchCHApps(options, envId)),
    dispatch(fetchHybridApps(options, envId)),
    dispatch(fetchRTFApps(options, envId))
  ]
  return Promise.all(promises).then(results => {
    return [...results[0], ...results[1], ...results[2]]
  })
}

export const fetchCHApps = (options: SelectorOptions, envId: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<ARMApplicationDetail[]> => {
    const orgId = getState().monitoring.auth.profile.organizationId
    const appService = new ApplicationService(orgId, api.arm(getState))

    return fetchLoaderHelper<ARMApplicationDetail[]>(dispatch, cloudhubAppActionCreators(options.origin), async () => {
      if (options.block && options.block.ch) {
        return Promise.resolve([])
      }

      let cloudhubApps = undefined

      if (options.withMetrics) {
        const apps = await getApps(orgId, envId, getState, {api}, options.withMetrics)
        cloudhubApps = apps.filter(app => app.targetType === 'app')
      }

      return appService.fetchCloudhubApplications(envId, cloudhubApps)
        .then((results: ARMApplicationDetail[]) => {
          const filter = options.filters && options.filters.resources

          if (filter) {
            return filter(results, envId)
              .then(filteredApps => filteredApps as ARMApplicationDetail[])
              .catch(() => results)
          } else {
            return results
          }
        })
    })
      .then(apps => apps)
      .catch(() => [])
  }

export const fetchHybridApps = (options: SelectorOptions, envId: string) =>
  async (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<ARMHybridDetail[]> => {
    const orgId = getState().monitoring.auth.profile.organizationId
    const appService = new ApplicationService(orgId, api.arm(getState))

    return fetchLoaderHelper<ARMHybridDetail[]>(dispatch, hybridAppActionCreators(options.origin), async () => {
      if (options.block && options.block.hybrid) {
        return Promise.resolve([])
      }

      let hybridApps = undefined

      if (options.withMetrics) {
        const apps = await getApps(orgId, envId, getState, {api}, options.withMetrics)
        hybridApps = apps.filter(app => isHybrid(app.targetType))
      }

      return appService.fetchHybridApplications(envId, hybridApps)
        .then(results => {
          const filter = options.filters && options.filters.resources

          if (filter) {
            return filter(results, envId)
              .then(filteredApps => filteredApps as ARMHybridDetail[])
              .catch(() => results)
          } else {
            return results
          }
        })
    })
      .then(apps => apps)
      .catch(() => [])
  }

export const fetchRTFApps = (options: SelectorOptions, envId: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<RTFApplication[]> => {
    const orgId = getState().monitoring.auth.profile.organizationId
    const appService = new ApplicationService(orgId, api.arm(getState))

    return fetchLoaderHelper<RTFApplication[]>(dispatch, rtfAppActionCreators(options.origin), async () => {
      if (options.block && options.block.rtf) {
        return Promise.resolve([])
      }

      return getApps(orgId, envId, getState, {api}, options.withMetrics).then((apps: Application[]) => {
        const rtfApps = apps.filter(app => isRTF(app.targetType))
        return appService.fetchRTFApplications(envId, rtfApps)
          .then(results => {
            const filter = options.filters && options.filters.resources

            if (filter) {
              return filter(results, envId)
                .then(filteredApps => filteredApps as RTFApplication[])
                .catch(() => results)
            } else {
              return results
            }
          })
      })
    })
      .then(apps => apps)
      .catch(() => [])
  }

export const fetchDetailedApp = (envId: string, app: ApplicationResource) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<ApplicationResource> => {
    const organizationId = getState().monitoring.auth.profile.organizationId
    const applicationService = new ApplicationService(organizationId, api.arm(getState))

    return applicationService.fetchDetailedApplication(envId, app)
  }

// Servers

export const targetsActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<ARMServer[]>(TARGET_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const fetchServers = (options: SelectorOptions, envId: string) => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const orgId = getState().monitoring.auth.profile.organizationId
  return fetchLoaderHelper<ARMServer[]>(dispatch, targetsActionCreators(options.origin), () => {
    if (options.block && options.block.ch) {
      return Promise.resolve([])
    }

    return api.arm(getState).fetchServers(orgId, envId).then(async (servers: {data: ARMServer[]}) => {
      const filter = options.filters && options.filters.resources
      let filteredServers = servers.data

      if (filter) {
        try {
          filteredServers = await filter(servers.data, envId) as ARMServer[]
        } catch (e) {
          filteredServers = []
        }
      }

      return filteredServers
    }).catch(() => [])
  })
}

// APIs

export const apisActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<Asset[]>(API_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const fetchAPIsWithToken = (envId: string, options: SelectorOptions) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<Asset[]> => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    return fetchLoaderHelper<Asset[]>(dispatch, apisActionCreators(options.origin), async () => {
      if (options.block && options.block.apis) {
        return Promise.resolve([])
      }

      if (options.httpMetricsCompatible) {
        return dispatch(fetchHTTPMetricsCompatibleAPIs(envId, options))
      }

      if (maybePlatformToken) {
        return dispatch(fetchAPIs(envId, options))
      } else {
        const apiPlatformRemoteAPI = api.apiPlatform(getState)
        return apiPlatformRemoteAPI.getAPIPlatformToken()
          .then(session => {
            dispatch(changePlatformToken(session.token))
            return dispatch(fetchAPIs(envId, options))
          })
      }
    })
      .then((assets: Asset[]) => assets)
      .catch(() => [])
  }

export const fetchHTTPMetricsCompatibleAPIs = (envId: string, options: SelectorOptions) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<Asset[]> => {
    const uiRemoteAPI = api.ui(getState)
    const orgId = getState().monitoring.auth.profile.organizationId

    return uiRemoteAPI.fetchAllAPIs(orgId, envId, true).then(assetsResponse => {
      const filter = options.filters && options.filters.resources

      if (filter) {
        return filter(assetsResponse.assets, envId)
          .then(assets => assets as Asset[])
          .catch(() => assetsResponse.assets)
      }

      return assetsResponse.assets
    })
}

export const fetchAPIs = (envId: string, options: SelectorOptions) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<Asset[]> => {
    const apiManagerRemoteAPI = api.apiManager(getState)
    const orgId = getState().monitoring.auth.profile.organizationId

    return apiManagerRemoteAPI
      .fetchAPIs(orgId, envId, {limit: 100, offset: 0}).then(async assetResponse => {
        const totalAssets = assetResponse.total
        let assets = assetResponse.assets

        const filter = options.filters && options.filters.resources
        if (filter) {
          try {
            assets = await filter(assets, envId) as Asset[]
          } catch (e) {
            assets = []
          }
        }

        const offsets = totalAssets / 100.0

        if (offsets > 1.0) {
          for (let i = 1; i < offsets; i++) {
            const offsetResponse = await apiManagerRemoteAPI.fetchAPIs(orgId, envId, {limit: 100, offset: i * 100})

            if (filter) {
              try {
                const offsetAssets = await filter(offsetResponse.assets, envId) as Asset[]
                assets = assets.concat(offsetAssets)
              } catch (e) {
                assets = assets.concat([])
              }
            } else {
              assets = assets.concat(offsetResponse.assets)
            }

            let loading = i + 1 < offsets
            if (loading) {
              dispatch(apisActionCreators(options.origin).fetchSuccess(assets))
            }
          }
        }

        return assets
      })
  }

export const fetchDetailedAPIWithToken = (envId: string, versionId: string, apiId: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<APIDetail> => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    if (maybePlatformToken) {
      return dispatch(fetchDetailedAPI(envId, versionId, apiId))
    } else {
      const apiPlatformRemoteAPI = api.apiPlatform(getState)
      return apiPlatformRemoteAPI.getAPIPlatformToken().then(session => {
        dispatch(changePlatformToken(session.token))
        return dispatch<Promise<APIDetail>>(fetchDetailedAPI(envId, versionId, apiId))
      })
    }
  }

const getAPIDetailStatus = (apiDetail: APIDetail): APIStatus => {
  let status = ''
  if (!apiDetail.lastActiveDate) {
    status = ENDPOINT_STATUS.unregistered
  } else {
    const activeDelta = apiDetail.lastActiveDelta || inactivityThreshold + 1
    status = activeDelta > inactivityThreshold ? ENDPOINT_STATUS.inactive : ENDPOINT_STATUS.active
  }
  return {
    fullDomain: '',
    lastActiveDate: apiDetail.lastActiveDate || '',
    status: status
  }
}

export const fetchDetailedAPI = (envId: string, versionId: string, apiId: string) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<APIDetail> => {
    const organizationId = getState().monitoring.auth.profile.organizationId
    const remoteAPIManager = api.apiManager(getState)

    return remoteAPIManager.fetchAPI(organizationId, envId, versionId)
      .then((apiDetail: APIDetail) => {
        if (apiDetail.deployment && apiDetail.deployment.id) {
          return api.xapiProxy(getState).fetchStatus(organizationId, envId, versionId, apiDetail.deployment.id)
            .then((apiStatus) => {
              return {...apiDetail, apiStatus: apiStatus, apiId: apiId}
            })
            .catch(() => {
              return {...apiDetail, apiStatus: getAPIDetailStatus(apiDetail), apiId: apiId}
            })
        } else {
          return {...apiDetail, apiStatus: getAPIDetailStatus(apiDetail), apiId: apiId}
        }
      })
  }

export const managedAPIsActionCreators = (origin?: string) => {
  return {
    ...fetchActionCreatorsFor<ManagedAPI[]>(MANAGED_API_RESOURCE_NAME, RESOURCE_PREFIX, origin)
  }
}

export const fetchManagedAPIsWithToken = (envId: string, options: SelectorOptions) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<ManagedAPI[]> => {
    const maybePlatformToken = authSelectors.getPlatformToken(getState())

    return fetchLoaderHelper<ManagedAPI[]>(dispatch, managedAPIsActionCreators(options.origin), async () => {
      if (options.block && options.block.apis) {
        return Promise.resolve([])
      }

      if (maybePlatformToken) {
        return dispatch(fetchManagedAPIs(envId, options))
      } else {
        const apiPlatformRemoteAPI = api.apiPlatform(getState)
        return apiPlatformRemoteAPI.getAPIPlatformToken()
          .then(session => {
            dispatch(changePlatformToken(session.token))
            return dispatch(fetchManagedAPIs(envId, options))
          })
      }
    })
      .then((managedAPIs: ManagedAPI[]) => managedAPIs)
      .catch(() => [])
  }

export const fetchManagedAPIs = (envId: string, options: SelectorOptions) =>
  (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs): Promise<ManagedAPI[]> => {
    const orgId = getState().monitoring.auth.profile.organizationId

    return api.apiManager(getState).fetchManagedAPI(orgId, envId)
      .then(async (result: {managedServiceApis: ManagedAPI[]}) => {
        const managedAPIs = result.managedServiceApis
        if (managedAPIs.length > 0) {
          let managedAPI = [{...managedAPIs[0], type: ResourceType.MANAGED_API}]
          const filter = options.filters && options.filters.resources

          if (filter) {
            try {
              managedAPI = await filter(managedAPI, envId) as ManagedAPI[]
            } catch (e) {
              managedAPI = []
            }
          }

          return managedAPI
        }

        return []
      })
  }

// Select resource

export const selectAnypointResource = (options: SelectorOptions, resources: Resource[], resourceId?: string, versionId?: string) =>
  (dispatch: Dispatch): Resource | undefined => {
    const resourceType = options.resourceType
    const actionCreators = resourceActionCreators(options.origin)
    const maybeResource = resourceId && resourceType && getResourceByIdOrInfluxId(resources, resourceId, resourceType, options.targetId)
    const maybeAPI = resourceType === ResourceType.API && versionId && getAPIFromAPIInstance(resources, versionId)
    const resId = maybeResource ? getResourceId(maybeResource) : maybeAPI ? getResourceId(maybeAPI) : resourceId

    return dispatch(selectResourceFromResources<Resource>(
      RESOURCE_RESOURCE_NAME,
      maybeResource ? [maybeResource] : resources,
      getResourceId,
      actionCreators.resourceSelected,
      options.origin,
      options.lsOrigin,
      resId
    ))
  }

export const storeResourcesAndSelectOne =
  (resource: Resource, resources: Resource[], origin?: string) =>
    (dispatch: Dispatch) => {
      const actionCreators = resourceActionCreators(origin)
      dispatch(actionCreators.fetchSuccess(resources))
      dispatch(actionCreators.resourceSelected(resource))
    }
